import type { OutcomeAssessmentTableRow } from "api/api-types-assessment";
import { tableDateFormat } from "components/materials/page/helpers";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { CommentIcon, ScoreIcon } from "mds/icons";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

interface OutcomeAssessmentTableProps {
  assessmentRows: OutcomeAssessmentTableRow[];
  title: string;
  showSectionColumn?: boolean;
}

/**
 * Displays a table of assessment data for pages with outcomes.
 * Can optionally show a course column for cross-course data.
 */
export const OutcomeAssessmentTable = ({
  assessmentRows,
  title,
  showSectionColumn = false,
}: OutcomeAssessmentTableProps) => {
  return (
    <div className="assessment-dashboard-table pb-6">
      <h2 className="h3 m-0 text-black-tint-40">{title}</h2>
      <table className="mds-table">
        <thead>
          <tr>
            {showSectionColumn && <th className="th-l">{t("glossary.section")}</th>}

            <th className="th-l">
              {t("assessment_dashboard.student_outcome_page_list.page_header_title")}
            </th>

            <th className="th-s">
              {t("assessment_dashboard.student_outcome_page_list.date_assessed_header_title")}
            </th>

            <th className="th-s">
              {t("assessment_dashboard.student_outcome_page_list.available_points_header_title")}
            </th>

            <th className="th-xs">
              {t("assessment_dashboard.student_outcome_page_list.score_header_title")}
            </th>
          </tr>
        </thead>
        <ErrorBoundary minimal>
          <tbody className="body-xs">
            {assessmentRows.map((pageAssessmentRow) => {
              const dateAssessed =
                pageAssessmentRow.assessment_created_at &&
                tableDateFormat(pageAssessmentRow.assessment_created_at);

              return (
                <tr key={pageAssessmentRow.page_id}>
                  {showSectionColumn && <td>{pageAssessmentRow.section_title}</td>}
                  <td className="flex w-full justify-start gap-2">
                    {showSectionColumn ? (
                      <span className="block truncate">{pageAssessmentRow.title}</span>
                    ) : (
                      <Link
                        className="block truncate"
                        to={pageUrl(pageAssessmentRow.section_id, pageAssessmentRow.page_id)}
                      >
                        {pageAssessmentRow.title}
                      </Link>
                    )}

                    {pageAssessmentRow.assessment_comment && (
                      <Tooltip
                        text={
                          <div className="body-xs text-black-tint-40">
                            {pageAssessmentRow.assessment_comment}
                          </div>
                        }
                      >
                        <CommentIcon />
                      </Tooltip>
                    )}
                  </td>

                  <td>{dateAssessed || "-"}</td>

                  <td>{pageAssessmentRow.max_points ?? "-"}</td>

                  <td>
                    {pageAssessmentRow.outcome_score ? (
                      <ScoreIcon value={pageAssessmentRow.outcome_score} />
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ErrorBoundary>
      </table>
    </div>
  );
};
